import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/60',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground stroke-destructive-foreground shadow-sm hover:bg-destructive/80',
        dark: 'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80',
        muted:
          'border-transparent bg-muted text-muted-foreground shadow-sm hover:bg-muted/80',
        info: 'border-transparent bg-info text-info-foreground shadow-sm hover:bg-info/80',
        outline: 'text-foreground',
        warning:
          'border-transparent bg-warning text-warning-foreground shadow-sm hover:bg-warning/80',
        success:
          'border-transparent bg-success text-success-foreground hover:bg-success/60',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const isTrimmableTextChild = (
  child: React.ReactNode
): child is string | number =>
  typeof child === 'string' || typeof child === 'number';

const hasOnlyTextChildren = (children: React.ReactNode) => {
  const normalizedChildren = React.Children.toArray(children);

  return (
    normalizedChildren.length > 0 &&
    normalizedChildren.every(isTrimmableTextChild)
  );
};

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function BadgeText({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      data-slot="badge-text"
      className={cn('block leading-none', className)}
      {...props}
    />
  );
}

function Badge({ className, variant, children, ...props }: BadgeProps) {
  const shouldTrimText = hasOnlyTextChildren(children);

  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {shouldTrimText ? <BadgeText>{children}</BadgeText> : children}
    </div>
  );
}

export { Badge, BadgeText, badgeVariants };
